import { useTranslation } from 'react-i18next';
import { OrderState } from '../standingOrderActions';
import styles from './AdditionalInfo.module.css';

const STATE_COLORS: Record<OrderState, string> = {
  enabled: 'var(--status-approved-border)',
  // The board's colour for a frozen order (HAT-1447).
  disabled: 'var(--status-pending-completion-border)',
  ended: 'var(--status-cancelled-border)',
  cancelled: 'var(--status-rejected-border)',
};

interface OrderStatusPanelProps {
  state: OrderState;
  /** Formatted for display, or null when Nedarim hold none. */
  nextDate: string | null;
  /** The monthly amount, formatted with its currency. */
  amount: string;
  /**
   * The last freeze done from the board, if the order is frozen because of one. `autoMonths` is set
   * when the declined-orders check froze it (HAT-1447): the months in a row it was refused.
   */
  frozen?: { date: string; by: string | null; autoMonths?: number } | null;
  /** The cancellation done from the board, if the order is cancelled because of one (HAT-1441: a bank order stays to be reactivated). */
  cancelled?: { date: string; by: string | null } | null;
  /**
   * Absent when the order cannot be frozen or restarted. A running order's button freezes it; any
   * other's restarts it - a frozen order, or (HAT-1441) a cancelled bank order.
   */
  onToggle?: () => void;
  /** A bank order in APT (HAT-1441): cancelled without a record of who did it, it is not "cancelled at Nedarim". */
  bank?: boolean;
  /** The reason given when the board froze or cancelled the order (HAT-1441), said after the rest. */
  reason?: string | null;
}

/**
 * Where the order stands, at the top of the blade, with the one button that changes it. The
 * board's status column says the same in a word; here it says what that means - when the next
 * charge is, or since when and by whom it has been frozen or cancelled - next to the way to undo it.
 */
const OrderStatusPanel = ({ state, nextDate, amount, frozen, cancelled, onToggle, bank = false, reason }: OrderStatusPanelProps) => {
  const { t } = useTranslation();
  const prefix = 'standingOrders.orderActions';

  let detail: string;
  if (state === 'enabled') {
    detail = nextDate ? t(`${prefix}.nextCharge`, { date: nextDate, amount }) : t(`${prefix}.perMonth`, { amount });
  } else if (state === 'disabled') {
    detail = frozen
      ? frozen.autoMonths != null
        ? t(`${prefix}.frozenAuto`, { date: frozen.date, count: frozen.autoMonths, amount })
        : frozen.by
          ? t(`${prefix}.frozenBy`, { date: frozen.date, name: frozen.by, amount })
          : t(`${prefix}.frozenOn`, { date: frozen.date, amount })
      : t(`${prefix}.frozenElsewhere`, { amount });
  } else if (state === 'ended') {
    detail = t(`${prefix}.endedNote`);
  } else if (cancelled) {
    detail = cancelled.by
      ? t(`${prefix}.cancelledBy`, { date: cancelled.date, name: cancelled.by })
      : t(`${prefix}.cancelledOn`, { date: cancelled.date });
  } else {
    detail = t(bank ? `${prefix}.bank.cancelledNote` : `${prefix}.cancelledNote`);
  }

  if (reason && (state === 'disabled' || state === 'cancelled')) {
    detail = `${detail} · ${t(`${prefix}.bank.reason.shown`, { reason })}`;
  }

  return (
    <div className={styles.statusPanel}>
      <div className={styles.statusText}>
        <span className={styles.statusLine}>
          <span className={styles.statusDot} style={{ backgroundColor: STATE_COLORS[state] }} />
          {t(`${prefix}.state_${state}`)}
        </span>
        <small className={styles.statusDetail}>{detail}</small>
      </div>
      {onToggle && (
        <button type="button" className={styles.cardUpdateButton} onClick={onToggle}>
          {t(state === 'enabled' ? `${prefix}.freeze` : `${prefix}.reactivate`)}
        </button>
      )}
    </div>
  );
};

export default OrderStatusPanel;
